import { type ApolloClient } from '@apollo/client';

import {
  type AttachMeetingRecordingInput,
  type CreateMeetingInput,
  type FileTransferAdapter,
  type GenerateDownloadUrlResult,
  type GenerateRecordingUploadUrlInput,
  type GenerateRecordingUploadUrlResult,
  type UploadUrlHeader,
} from '@/adapters/fileTransfer/types';
import { putFile } from '@/adapters/fileTransfer/uploadTransport';
import { sentryLogger } from '@/adapters/logger/sentryLogger';
import {
  AttachMeetingRecordingDocument,
  CreateMeetingDocument,
  GenerateContactFileDownloadUrlDocument,
  GenerateRecordingUploadUrlDocument,
} from '@/generated/usermobile-bff/graphql';

const QUOTA_USAGE_FIELD_NAME = 'meetingSummaryQuotaUsage';

const evictQuotaUsage = (apolloClient: ApolloClient): void => {
  apolloClient.cache.evict({ fieldName: QUOTA_USAGE_FIELD_NAME });
  apolloClient.cache.gc();
};

const UPLOAD_TRANSPORT_ERROR_NAME = 'UploadTransportError';

const createUploadTransportError = (message: string): Error => {
  const error = new Error(message);
  error.name = UPLOAD_TRANSPORT_ERROR_NAME;

  return error;
};

const isUploadTransportError = (error: unknown): boolean =>
  error instanceof Error && error.name === UPLOAD_TRANSPORT_ERROR_NAME;

const toHeaderRecord = (
  headers: readonly UploadUrlHeader[],
  mimeType: string,
): Record<string, string> =>
  headers.reduce<Record<string, string>>(
    (accumulated, header) => {
      if (header.name.toLowerCase() === 'host') {
        return accumulated;
      }

      return { ...accumulated, [header.name]: header.values.join(', ') };
    },
    { 'Content-Type': mimeType },
  );

const createFileTransferAdapter = (apolloClient: ApolloClient): FileTransferAdapter => ({
  generateRecordingUploadUrl: async (
    input: GenerateRecordingUploadUrlInput,
  ): Promise<GenerateRecordingUploadUrlResult> => {
    const { data } = await apolloClient.mutate({
      mutation: GenerateRecordingUploadUrlDocument,
      variables: { input: { uuid: input.uuid, fileExtension: input.fileExtension } },
    });

    if (!data) {
      throw new Error('generateRecordingUploadUrl: no data returned');
    }

    const { uploadUrl, storagePath, headers } = data.generateRecordingUploadUrl;

    return { uploadUrl, storagePath, headers };
  },

  uploadFile: (
    uploadUrl: string,
    headers: readonly UploadUrlHeader[],
    fileUri: string,
    mimeType: string,
  ): Promise<void> => {
    const maxAttempts = 3;
    const headerRecord = toHeaderRecord(headers, mimeType);

    const attempt = async (n: number): Promise<void> => {
      sentryLogger.addBreadcrumb('uploadFile: starting background PUT', {
        uploadUrl,
        fileUri,
        mimeType,
        attempt: n,
      });

      const result = await putFile(uploadUrl, headerRecord, fileUri, mimeType).catch(
        (error: unknown) => {
          sentryLogger.addBreadcrumb('uploadFile: upload task rejected', {
            message: error instanceof Error ? error.message : String(error),
            attempt: n,
          });

          throw createUploadTransportError(
            `S3 upload network error: ${error instanceof Error ? error.message : String(error)}`,
          );
        },
      );

      sentryLogger.addBreadcrumb('uploadFile: upload task settled', {
        status: result.status,
        body: result.body,
        attempt: n,
      });

      if (result.status >= 200 && result.status < 300) {
        return;
      }

      throw new Error(`S3 upload failed with status ${result.status}: ${result.body}`);
    };

    const attemptWithRetry = (n: number): Promise<void> =>
      attempt(n).catch((error: unknown) => {
        if (isUploadTransportError(error) && n < maxAttempts) {
          return attemptWithRetry(n + 1);
        }

        throw error;
      });

    return attemptWithRetry(1);
  },

  createMeeting: async (
    input: CreateMeetingInput,
  ): Promise<{ id: string; recording?: { id: string; uuid: string } }> => {
    const { data } = await apolloClient.mutate({
      mutation: CreateMeetingDocument,
      variables: {
        input: {
          contactIds: input.contactIds.length > 0 ? input.contactIds.map(String) : undefined,
          startsAt: input.startsAt,
          endsAt: input.endsAt,
          location: input.location,
          recording: input.recording,
        },
      },
    });

    if (!data) {
      throw new Error('createMeeting: no data returned');
    }

    evictQuotaUsage(apolloClient);

    const { createMeeting } = data;

    return {
      id: createMeeting.id,
      recording: createMeeting.recording
        ? { id: createMeeting.recording.id, uuid: createMeeting.recording.uuid }
        : undefined,
    };
  },

  attachMeetingRecording: async (input: AttachMeetingRecordingInput): Promise<{ id: string }> => {
    const { data } = await apolloClient.mutate({
      mutation: AttachMeetingRecordingDocument,
      variables: {
        input: {
          meetingId: input.meetingId,
          recording: input.recording,
        },
      },
    });

    if (!data) {
      throw new Error('attachMeetingRecording: no data returned');
    }

    evictQuotaUsage(apolloClient);

    return { id: data.attachMeetingRecording.id };
  },

  generateDownloadUrl: async (fileId: string): Promise<GenerateDownloadUrlResult> => {
    const { data } = await apolloClient.mutate({
      mutation: GenerateContactFileDownloadUrlDocument,
      variables: { input: { fileId } },
    });

    if (!data) {
      throw new Error('generateContactFileDownloadUrl: no data returned');
    }

    const { url } = data.generateContactFileDownloadUrl;

    return { url };
  },
});

export { createFileTransferAdapter };
